import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { Loading, Pecten, Sizes } from '@sede-enterprise/shell-ds-react-framework';
import dayjs from 'dayjs';
import jwtDecode from 'jwt-decode';
import { HomeWrapper } from './style';
import getAccessTokenFromCode from '../../utils/auth/getAccessTokenFromCode';
import getAccessTokenFromCodeRefreshToken from '../../utils/auth/getAccessTokenFromCodeRefreshToken';
import { userRoleActions } from '../../store/reducers/GenesisUser';
import routepaths from '../../router/routepaths';
import { IUserInfo } from '../../types/UserState.type';

const Base: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch user info and token
  const fetchUserInfo = useCallback(
    async (code: string | undefined) => {
      const { user, accessToken, error } = await getAccessTokenFromCode(code);
      if (error) {
        navigate(routepaths.login);
      } else if (user) {
        const userObj = {
          sn: user.sn,
          givenName: user.givenName,
          uid: user.uid,
          accessToken,
          mail: user.mail,
        };
        dispatch(userRoleActions.setNameAccessToken(userObj));
        navigate(routepaths.authenticate);
      }
    },
    [navigate, dispatch],
  );

  const refreshAccessToken = useCallback(async () => {
    const refreshToken = sessionStorage.getItem('refresh_token');
    const { user, accessToken, error } = await getAccessTokenFromCodeRefreshToken(
      refreshToken || '',
    );
    if (error) {
      navigate(routepaths.login);
    } else if (user) {
      const userObj = {
        sn: user.sn,
        givenName: user.givenName,
        uid: user.uid,
        accessToken,
        mail: user.mail,
      };

      dispatch(userRoleActions.setNameAccessToken(userObj));
      navigate(routepaths.authenticate);
    }
  }, [navigate, dispatch]);

  useEffect(() => {
    const searchParams = new URLSearchParams(globalThis.location?.search ?? '');

    if (searchParams.get('code') && searchParams.get('state')) {
      const code = searchParams.get('code') || undefined;
      fetchUserInfo(code);
    } else {
      const accessToken = sessionStorage.getItem('access_token');
      const expiryTime = sessionStorage.getItem('expiry_time');
      if (accessToken && expiryTime) {
        const exp = dayjs.unix(Number.parseInt(expiryTime, 10));
        if (exp.isAfter(dayjs())) {
          const decodedToken = jwtDecode<IUserInfo>(accessToken);
          const userObj = {
            sn: decodedToken.sn,
            givenName: decodedToken.givenName,
            uid: decodedToken.uid,
            accessToken,
            mail: decodedToken.mail,
          };
          dispatch(userRoleActions.setNameAccessToken(userObj));

          navigate(routepaths.authenticate);
        } else {
          refreshAccessToken();
        }
      } else {
        navigate(routepaths.login);
      }
    }
  }, [dispatch, navigate, refreshAccessToken, fetchUserInfo]);

  return (
    <HomeWrapper>
      <Pecten size={Sizes.Large} />
      <h1 className='title'>Genesis</h1>
      <Loading size='medium' className='loader' />
      <div className='login-message'>Logging in...</div>
    </HomeWrapper>
  );
};

export default Base;
